/**
 * planSpeed.ts
 *
 * Speed formatting for customer-service surfaces.
 *
 * A `CustomerService` only denormalises `planName`, so "NBN Home Fast" tells
 * the office nothing about what the customer actually bought. The speed lives
 * in two different places depending on the service:
 *
 *   1. `service_plans.downloadSpeed` / `.uploadSpeed` — free-text strings the
 *      admin types in Settings → Plans ("100 Mbps", "1 Gbps", "Unlimited").
 *      This is the ADVERTISED speed and is what we show first.
 *   2. `CustomerService.metadata.downloadSpeedKbps` / `.uploadSpeedKbps` —
 *      the provisioned AVC profile synced from Utilmate. Used as a fallback
 *      when the plan carries no speed, so an untagged plan still shows
 *      something real.
 *
 * Deliberately NOT denormalised onto the service doc: plan speeds get edited
 * (a plan is re-tiered, a typo is fixed) and a stamped copy would silently go
 * stale on every existing customer. The lookup is live.
 *
 * Pure functions — unit tested in src/utils/__tests__/planSpeed.test.ts.
 */

import type { ServicePlan } from '@/types/schema';

export type PlanSpeedLike = Pick<ServicePlan, 'downloadSpeed' | 'uploadSpeed'>;

export interface ServiceSpeedMetadata {
  downloadSpeedKbps?: number;
  uploadSpeedKbps?: number;
}

export interface ResolvedSpeed {
  /** Compact label, e.g. "100/20 Mbps". */
  label: string;
  /** Where the number came from — drives the tooltip so nobody quotes the wrong one. */
  source: 'plan' | 'network';
}

export const SPEED_SOURCE_TITLE: Record<ResolvedSpeed['source'], string> = {
  plan: 'Advertised plan speed (download/upload), from Settings → Plans',
  network:
    'Provisioned speed profile synced from the carrier — the plan has no advertised speed set',
};

/**
 * "100 Mbps" → 100 · "1 Gbps" → 1000 · "1000 Mbps (1 Gbps)" → 1000 · "100" → 100.
 * Anything that doesn't START with a number ("Unlimited", "Up to 100 Mbps")
 * returns null so the caller can fall back to printing the raw string.
 */
function parseMbps(raw?: string): number | null {
  if (!raw) return null;
  const m = raw.trim().match(/^(\d+(?:\.\d+)?)\s*(g|m)?(?:bps|b\/s|b)?\b/i);
  if (!m) return null;
  const n = parseFloat(m[1]);
  if (!Number.isFinite(n) || n <= 0) return null;
  return m[2]?.toLowerCase() === 'g' ? n * 1000 : n;
}

/** Whole numbers stay whole; anything else keeps one decimal (12.5 Mbps). */
function trimNum(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
}

/**
 * The plan's advertised speed as a compact label, or null when the plan has
 * none. Both speeds are normalised to Mbps because that's how AU plans are
 * quoted ("1000/50"), even when the plan doc says "1 Gbps".
 */
export function formatPlanSpeed(plan?: PlanSpeedLike | null): string | null {
  if (!plan) return null;
  const down = (plan.downloadSpeed || '').trim();
  const up = (plan.uploadSpeed || '').trim();
  if (!down && !up) return null;

  const d = parseMbps(down);
  const u = parseMbps(up);

  if (d != null && u != null) return `${trimNum(d)}/${trimNum(u)} Mbps`;
  if (d != null && !up) return `${trimNum(d)} Mbps`;

  // One side is non-numeric ("Unlimited") — print what the plan actually says
  // rather than inventing a number.
  return [down, up].filter(Boolean).join(' / ');
}

/**
 * The provisioned speed profile from Utilmate (kbps on the service metadata).
 * Requires a download speed — an upload-only value would render as if it were
 * the download, which is worse than showing nothing.
 */
export function formatNetworkSpeed(meta?: ServiceSpeedMetadata | null): string | null {
  const dKbps = meta?.downloadSpeedKbps;
  const uKbps = meta?.uploadSpeedKbps;
  if (typeof dKbps !== 'number' || !(dKbps > 0)) return null;

  const d = dKbps / 1000;
  const u = typeof uKbps === 'number' && uKbps > 0 ? uKbps / 1000 : null;
  return u != null ? `${trimNum(d)}/${trimNum(u)} Mbps` : `${trimNum(d)} Mbps`;
}

/**
 * The speed to show against a customer service: the plan's advertised speed
 * when it has one, else the carrier-provisioned profile. Null when neither
 * exists (one-time installs, VoIP, Security, etc. — nothing to show).
 */
export function resolveServiceSpeed(
  plan?: PlanSpeedLike | null,
  metadata?: ServiceSpeedMetadata | null,
): ResolvedSpeed | null {
  const fromPlan = formatPlanSpeed(plan);
  if (fromPlan) return { label: fromPlan, source: 'plan' };

  const fromNetwork = formatNetworkSpeed(metadata);
  if (fromNetwork) return { label: fromNetwork, source: 'network' };

  return null;
}

/**
 * `"NBN Home Fast · 100/20 Mbps"` — plan name with its speed appended, for
 * dropdown options and one-line summaries. Falls back to the bare name.
 */
export function planLabelWithSpeed(
  plan: PlanSpeedLike & { displayName?: string; planName?: string },
): string {
  const name = plan.displayName || plan.planName || 'Plan';
  const speed = formatPlanSpeed(plan);
  return speed ? `${name} · ${speed}` : name;
}
